import { useEffect } from 'react';

import type { DataLayerItem, EventWithAnalytics } from '../../types';

import { hasDataLayer } from '../../utils/has-data-layer';
import { pushDataToGtmFromDataset } from '../../utils/push-data-ga-from-dataset';

// ---

declare global {
  interface Window {
    dataLayer: DataLayerItem[];
  }
}

type UseAnalyticsProps = {
  active: boolean;
  additionalDataForGA: DataLayerItem;
};

const useAnalytics = ({ active, additionalDataForGA }: UseAnalyticsProps) => {
  const analyticsHandler = (event: Event) => {
    if (!isHTMLElementEvent(event.target) || !hasDataLayer()) {
      return;
    }

    const target = event.target.closest('[data-ga-event]');

    if (!(target instanceof HTMLElement)) {
      return;
    }

    pushDataToGtmFromDataset(target, additionalDataForGA);
  };

  const analyticsEventHandler = (event: EventWithAnalytics) => {
    if (!hasAnalyticsEvent(event) || !hasDataLayer()) {
      return;
    }

    const gaData: DataLayerItem = { ...additionalDataForGA, ...event.dataGaValues };

    window.dataLayer.push(gaData);
  };

  useEffect(() => {
    if (!active) {
      return;
    }

    document.addEventListener('click', analyticsHandler);
    document.addEventListener('analytics-event', analyticsEventHandler);

    return () => {
      document.removeEventListener('click', analyticsHandler);
      document.removeEventListener('analytics-event', analyticsEventHandler);
    };
  }, [additionalDataForGA]);
};

function hasAnalyticsEvent(event: EventWithAnalytics) {
  return event.dataGaValues && 'event' in event.dataGaValues;
}

function isHTMLElementEvent(target: EventTarget | null): target is HTMLElement {
  return !(target instanceof HTMLInputElement) || target.type === 'checkbox';
}

export default useAnalytics;
